import { z } from 'zod';
import { Rfc3339DateTime } from './create-chat.js';

/**
 * Shared, **versioned** wire contract for the `ui` BFF → `workstreamagent`
 * (Python) cross-module invoke boundary (D4-14 / ADR-0029 / ADR-0030).
 *
 * The BFF invokes the Python function's `live` alias synchronously
 * (`RequestResponse`) and validates the reply against {@link AgentInvokeResponseSchema}.
 * One source of truth both sides reason about — the BFF imports it directly; the
 * Python handler's echo shape is the same contract transcribed in Python.
 *
 * **Expand-contract (ADR-0029).** {@link AGENT_CONTRACT_VERSION} pins the request
 * shape; bump it only on a breaking change (additive fields don't bump it). The
 * **response** is **tolerant-out** (`z.looseObject`) so the agent module can add
 * fields and roll forward/back independently of the BFF — a new agent field never
 * breaks an old BFF, mirroring the ADR-0024 §11 response posture.
 *
 * NB (D4-13 reconciliation): the deployed Python function is a minimal structured
 * **echo** (no model/inference yet), so the response carries `echo` rather than a
 * score. When the real agentic handler lands, it adds fields under the same
 * tolerant-out response — no BFF change required, no version bump.
 */

/**
 * The BFF→Python invoke-contract version. A breaking change to
 * {@link AgentInvokeRequestSchema} bumps this; additive optional fields do not.
 */
export const AGENT_CONTRACT_VERSION = 1;

/** Generous cap on the free-text probe message — bounds the invoke payload (OWASP API4). */
const MAX_MESSAGE_LEN = 10_000;

/**
 * The payload the BFF sends as the Lambda invoke event. Strict — the BFF authors
 * it, so an unexpected key is a bug. `requestId` doubles as the **idempotency /
 * correlation key** carried into the callee (the agent honours it once it becomes
 * stateful; synchronous `RequestResponse` is not auto-retried, so this is the
 * only dedup seam — D4-14 sync-client policy).
 */
export const AgentInvokeRequestSchema = z.strictObject({
  contractVersion: z.literal(AGENT_CONTRACT_VERSION),
  requestId: z.string().min(1),
  occurredAt: Rfc3339DateTime,
  message: z.string().min(1).max(MAX_MESSAGE_LEN),
});
export type AgentInvokeRequest = z.infer<typeof AgentInvokeRequestSchema>;

/**
 * The reply the Python function returns. **Tolerant-out** (`z.looseObject`): the
 * agent module may add fields and the BFF parse must survive them (expand-contract,
 * ADR-0029). Fixed fields are the D4-13 echo shape (`ok`/`module`/`version`/
 * `requestId`, plus the echoed event under `echo`).
 */
export const AgentInvokeResponseSchema = z.looseObject({
  ok: z.boolean(),
  module: z.string(),
  version: z.string(),
  requestId: z.string(),
  echo: z.unknown().optional(),
});
export type AgentInvokeResponse = z.infer<typeof AgentInvokeResponseSchema>;

/* ── HTTP ping route — POST /api/v1/workstream/agent/ping ────────────────── */

/**
 * Request body for the diagnostic ping route that exercises the BFF→Python seam.
 * `message` is optional (the BFF defaults it); strict so a stray key is rejected
 * at the edge (the same discipline as the create-flow requests).
 */
export const AgentPingRequestSchema = z.strictObject({
  message: z.string().min(1).max(MAX_MESSAGE_LEN).optional(),
});
export type AgentPingRequest = z.infer<typeof AgentPingRequestSchema>;

/**
 * The `data` payload of the ping response — the agent's (tolerant-out) reply.
 * `data` itself stays strict (fixed shape; the tolerance is on the nested
 * `agent` per the response contract above), wrapped in the standard `{ data, meta }`
 * envelope by the handler.
 */
export const AgentPingDataSchema = z.strictObject({
  agent: AgentInvokeResponseSchema,
});
export type AgentPingData = z.infer<typeof AgentPingDataSchema>;
